import React, { createContext, useContext, useEffect, useRef, useMemo } from 'react';
import { useSystemNotifications as useSystemNotificationsQuery, useMarkNotificationRead } from 'hooks/systemNotificationsAPI';
import { useToast } from 'components/contexts/ToastContext';
import { useRehiveContext } from './RehiveContext';

const SystemNotificationsContext = createContext({
  notifications: [],
  unreadCount: 0,
  isLoading: true,
  markAsRead: () => {},
  markAllAsRead: () => {},
  refetch: () => {},
});

export function useSystemNotifications() {
  const context = useContext(SystemNotificationsContext);
  if (context === undefined) {
    throw new Error('useSystemNotifications must be used within a SystemNotificationsProvider');
  }
  return context;
}

export function SystemNotificationsProvider({ children }) {
  const { showToast } = useToast();
  const { user, services } = useRehiveContext();
  const hasShownToast = useRef(false);
  
  const hasNotificationsService = services?.notifications_service;
  const enabled = !!user?.id && hasNotificationsService;
  
  const { data, isLoading, refetch } = useSystemNotificationsQuery(enabled);
  const markReadMutation = useMarkNotificationRead();
  
  const notifications = useMemo(() => {
    return data?.data?.results || data?.results || [];
  }, [data]);
  
  const unreadCount = useMemo(() => {
    return notifications.filter(n => n.received !== true).length;
  }, [notifications]);
  
  // Show toast on initial load if there are unread notifications
  useEffect(() => {
    if (!isLoading && unreadCount > 0 && !hasShownToast.current) {
      hasShownToast.current = true;

      if (unreadCount > 3) {
        // Grouped message when more than 3 notifications
        showToast({
          text: `You have ${unreadCount} new notifications`,
          variant: 'info',
          duration: 5000,
        });
      } else {
        // Individual toasts for 1-3 notifications
        const unreadNotifications = notifications.filter(n => n.received !== true);
        unreadNotifications.forEach((notification, index) => {
          const title = notification.subject || notification.notification || 'New notification';
          const body = notification.push_message || notification.text_message || '';

          setTimeout(() => {
            showToast({
              text: body ? (
                <>
                  <strong>{title}</strong>
                  <div>{body}</div>
                </>
              ) : title,
              variant: 'info',
              duration: 5000,
            });
          }, index * 300);
        });
      }
    }
  }, [isLoading, unreadCount, notifications, showToast]);
  
  const markAsRead = async (id) => {
    try {
      await markReadMutation.mutateAsync(id);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };
  
  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter(n => n.received !== true);
    try {
      await Promise.all(unreadNotifications.map(n => markReadMutation.mutateAsync(n.id)));
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };
  
  const value = {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    refetch,
  };
  
  return (
    <SystemNotificationsContext.Provider value={value}>
      {children}
    </SystemNotificationsContext.Provider>
  );
}

export { SystemNotificationsContext };
